// src/encounter/services/l360-api.ts
// L360 A1-A3: the calls behind the activation panel. A1/A2 use the Patient host (`/api/Patients/...` on the PM-API
// host); A3 uses the Workflows host through the same api-client helpers care-model-api.ts uses. No fetch in components: the hooks in use-l360-activation.ts
// are the only callers.
import { env } from '@/config/env';
import { ApiError, get, getWorkflows, post, postWorkflows } from '@/shared/api/api-client';
import type { CarePlanWire } from '@/types/care-model-api';
import type {
  A3ConflictWire,
  CareModelRunWire,
  CareTeamResponse,
  ClaimTaskRequest,
  ClinicalTaskWire,
  CompleteA3Request,
  CompleteA3Result,
  PatientActivationSnapshot,
  CreateTeamTaskRequest,
  L360EventRequest,
  L360EventResult,
  L360EventType,
  RaiseHoldRequest,
  RunEnvelope,
  SignGoalRequest,
  TransitionHoldRequest,
  TransitionHoldResult,
} from '@/types/l360-api';

const planPath = (patientId: string, carePlanId: string) =>
  `/api/Patients/${encodeURIComponent(patientId)}/care-plans/${encodeURIComponent(carePlanId)}`;

/** A2 · GET .../team. Anything but `{ members: [...] }` is a contract break and a failed read. */
export async function fetchCareTeam(patientId: string, carePlanId: string, signal?: AbortSignal): Promise<CareTeamResponse> {
  const res = await get<CareTeamResponse>(`${planPath(patientId, carePlanId)}/team`, { signal });
  if (!res || !Array.isArray(res.members)) throw new Error('team: response is not the A2 envelope');
  return res;
}

/**
 * A3 · Patient's GET .../care-plans/{carePlanId}/activation-snapshot. 404 means the endpoint is not deployed
 * yet: null, and the approve body then leaves `expectedPatientRevisions` out. Any other failure, 503 included (Patient is down), is a failed read.
 */
export async function fetchActivationSnapshot(
  patientId: string,
  carePlanId: string,
  signal?: AbortSignal,
): Promise<PatientActivationSnapshot | null> {
  try {
    const res = await get<PatientActivationSnapshot>(`${planPath(patientId, carePlanId)}/activation-snapshot`, { signal });
    const ok = (v: unknown) => typeof v === 'string' || typeof v === 'number';
    if (!res || !ok(res.planRevision) || !ok(res.consentRevision)) throw new Error('activation-snapshot: response is not the snapshot');
    return res;
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404)) return null;
    throw e;
  }
}

/** A1 · sign one proposed target. 412 `stale-version` rejects (ApiError); the caller refreshes the plan. */
export function signGoal(
  patientId: string,
  carePlanId: string,
  goalId: string,
  body: SignGoalRequest,
): Promise<{ alreadySigned?: boolean; plan?: CarePlanWire }> {
  return post(`${planPath(patientId, carePlanId)}/goals/${encodeURIComponent(goalId)}/sign`, body);
}

/** The EXISTING POST api/Patients/{patientId}/Tasks. */
export function createTask(patientId: string, body: CreateTeamTaskRequest): Promise<ClinicalTaskWire> {
  return post<ClinicalTaskWire>(`/api/Patients/${encodeURIComponent(patientId)}/Tasks`, body);
}

/** The EXISTING POST .../Tasks/{taskId}/Claim. */
export function claimTask(patientId: string, taskId: string, body: ClaimTaskRequest): Promise<ClinicalTaskWire> {
  return post<ClinicalTaskWire>(`/api/Patients/${encodeURIComponent(patientId)}/Tasks/${encodeURIComponent(taskId)}/Claim`, body);
}

/** The id a 409 `duplicate-open-task` names (`existingTaskId`), or null when the error is anything else. */
export function duplicateTaskId(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  try {
    const body = JSON.parse(error.body) as { code?: unknown; existingTaskId?: unknown } | null;
    return body?.code === 'duplicate-open-task' && typeof body.existingTaskId === 'string' ? body.existingTaskId : null;
  } catch {
    return null;
  }
}

/** True for the server refusing a stale snapshot or revision (412). Nothing was written. */
export function isStale412(error: unknown): boolean {
  return error instanceof ApiError && error.status === 412;
}

// ---- A3: the Workflows A3 gate is the only decision point. Same client and host as care-model-api.ts. ----

const runPath = (enrolmentId: string) => `/workflows/care-models/enrolments/${encodeURIComponent(enrolmentId)}`;

/** Workflows wraps every response in `ResponseModel`; a bare body (a 409) is read as it is. */
function unwrap<T>(raw: unknown): T | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = (raw as { data?: unknown }).data;
  return (data && typeof data === 'object' ? data : raw) as T;
}

/** A3 · GET .../run. Anything but a `read` outcome with the four revisions is a failed read. */
export async function fetchCareModelRun(enrolmentId: string, signal?: AbortSignal): Promise<CareModelRunWire> {
  const res = await getWorkflows<RunEnvelope<CareModelRunWire>>(`${runPath(enrolmentId)}/run`, { signal });
  const run = res?.data;
  if (!run || run.outcome !== 'read' || !run.revisions || !Array.isArray(run.waitingOn)) {
    throw new Error('care-model run: response is not the run envelope');
  }
  return { ...run, holds: run.holds ?? [], log: run.log ?? [] };
}

/** A3 · POST .../gates/A3/complete. 200 routed; 409 with outcome `held`, `stale` or `consent-required` is a result, never a throw, never retried here. */
export async function postA3Complete(enrolmentId: string, body: CompleteA3Request): Promise<CompleteA3Result> {
  const patientSnapshot = body.expectedPatientRevisions ?? null;
  try {
    const res = await postWorkflows<unknown>(`${runPath(enrolmentId)}/gates/A3/complete`, body);
    const ok = unwrap<A3ConflictWire>(res);
    return { kind: 'routed', snapshot: body.expectedRevisions, patientSnapshot, patientRevisions: ok?.patientRevisions ?? null };
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 409) throw e;
    // Only a body that says `held`, `stale` or `consent-required` is a result. Anything else (no outcome, another outcome, an unreadable
    // body) is not understood, so it is thrown and the UI says nothing was changed.
    let parsed: A3ConflictWire | null = null;
    try {
      parsed = unwrap<A3ConflictWire>(JSON.parse(e.body));
    } catch {
      throw e;
    }
    if (parsed?.outcome !== 'held' && parsed?.outcome !== 'stale' && parsed?.outcome !== 'consent-required') throw e;
    return { kind: parsed.outcome, body: parsed, snapshot: body.expectedRevisions, patientSnapshot };
  }
}

/** A3 · POST .../holds: the demo hold. 201 on success. */
export function postHold(enrolmentId: string, body: RaiseHoldRequest): Promise<unknown> {
  return postWorkflows(`${runPath(enrolmentId)}/holds`, body);
}

/** A snapshot with `holds` and `log` always present. */
const toRun = (raw: unknown): CareModelRunWire => {
  const run = (unwrap<Partial<CareModelRunWire>>(raw) ?? {}) as CareModelRunWire;
  return { ...run, holds: run.holds ?? [], log: run.log ?? [] };
};

/**
 * A3 · POST .../holds/{holdId}/transition: ONE step. 200 recorded; a 409 whose body says `stale` is a result (the hold
 * revision moved). Anything else, including any other 409, is thrown.
 */
export async function transitionHold(enrolmentId: string, holdId: string, body: TransitionHoldRequest): Promise<TransitionHoldResult> {
  try {
    const res = await postWorkflows<unknown>(`${runPath(enrolmentId)}/holds/${encodeURIComponent(holdId)}/transition`, body);
    return { kind: 'recorded', run: toRun(res) };
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 409) throw e;
    let parsed: unknown;
    try {
      parsed = JSON.parse(e.body);
    } catch {
      throw e;
    }
    if (unwrap<{ outcome?: unknown }>(parsed)?.outcome !== 'stale') throw e;
    return { kind: 'stale', run: toRun(parsed) };
  }
}

// ---- Connected-flow events: one ingest endpoint on Workflows. ----

/** The `reason` of a 409 body (bare or under `data`), or null when the body is not readable. */
function refusalReason(body: string): string | null {
  try {
    const reason = unwrap<{ reason?: unknown }>(JSON.parse(body))?.reason;
    return typeof reason === 'string' ? reason : null;
  } catch {
    return null;
  }
}

/**
 * POST /workflows/care-models/events: tell the L360 run that a screen just saved something. Call it only AFTER the
 * caller's own save succeeded, and never await it where it could block or fail that save: callers use
 * `void postL360Event(...)`. It never throws and shows no toast or error (the api-client raises none); the result and
 * the console are the only trace. Off unless `env.l360EventsEnabled` (dev only): then no request is made.
 * 202 accepted, 204 no active enrolment, 409 refused (never retried). Any other HTTP error is `failed`, not retried.
 * A network error is retried once with the same body, so the same idempotency key: a duplicate is one effect.
 */
export async function postL360Event(input: {
  eventType: L360EventType;
  patientId: string;
  sourceRef: string;
  revision: string | number;
  payload: Record<string, unknown>;
  occurredAt?: string;
}): Promise<L360EventResult> {
  if (!env.l360EventsEnabled) return { kind: 'disabled' };
  try {
    const body: L360EventRequest = {
      eventType: input.eventType,
      patientId: input.patientId,
      sourceRef: input.sourceRef,
      idempotencyKey: `${input.eventType}:${input.sourceRef}:${input.revision}`,
      occurredAt: input.occurredAt ?? new Date().toISOString(),
      payload: input.payload,
    };
    // Only the post itself sits inside the retried try: a TypeError from reading the response can never post twice.
    let res: unknown;
    for (let attempt = 0; ; attempt++) {
      try {
        res = await postWorkflows<unknown>('/workflows/care-models/events', body);
        break;
      } catch (e) {
        if (e instanceof ApiError) {
          if (e.status === 409) return { kind: 'refused', reason: refusalReason(e.body) };
          console.warn(`L360 event ${input.eventType} failed (${e.status})`);
          return { kind: 'failed' };
        }
        // Only a fetch network error (TypeError) is retried: once, after 500 ms, with the same body (same key).
        // Anything else is a bug here, not a network drop, so it is not posted twice.
        if (e instanceof TypeError && attempt === 0) {
          await new Promise((r) => setTimeout(r, 500));
          continue;
        }
        console.warn(`L360 event ${input.eventType} failed`, e);
        return { kind: 'failed' };
      }
    }
    const ok = unwrap<{ enrolmentId?: unknown; node?: unknown; outcome?: unknown }>(res);
    if (!ok) return { kind: 'no-enrolment' };
    const str = (v: unknown) => (typeof v === 'string' ? v : undefined);
    return { kind: 'accepted', enrolmentId: str(ok.enrolmentId), node: str(ok.node), outcome: str(ok.outcome) };
  } catch (e) {
    console.warn(`L360 event ${input.eventType} failed`, e);
    return { kind: 'failed' };
  }
}
